"use client"

import { useState, useMemo } from "react"
import Image from "next/image"
import { motion, AnimatePresence, Variants } from "framer-motion"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface ServiceCardProps {
  category: string
  title: string
  description: string
  images: string[]
  accentColor: string
  reverse?: boolean
}

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
  }),
  center: {
    x: 0,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 1000 : -1000,
  }),
}

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

export function ServiceCard({ category, title, description, images, accentColor, reverse = false }: ServiceCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const [isDragging, setIsDragging] = useState(false)


  const getImageStyle = useMemo(() => {
    const currentImage = images[currentIndex]
    
    // Проверяем, являются ли это проблемные изображения
    const isDolphinImage = 
      currentImage === "/images/boats/5.webp"
    
      const isWeddingImage = 
      currentImage === "/images/wedding/3.webp"

      const isBoat = 
      currentImage === "/images/boats/3.webp"
      
    if (isDolphinImage) {
      return {
        objectFit: "cover" as const,
        objectPosition: "10% 10%" as const
      }
    }

    if (isWeddingImage) {
      return {
        objectFit: "cover" as const,
        objectPosition: "80% 10%" as const
      }
    }

    if (isBoat) {
      return {
        objectFit: "cover" as const,
        objectPosition: "0% 10%" as const
      }
    }
    
    return {
      objectFit: "cover" as const,
      objectPosition: "center" as const
    }
  }, [currentIndex, images])

  const paginate = (newDirection: number) => {
    setDirection(newDirection)
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + newDirection
      if (nextIndex < 0) return images.length - 1
      if (nextIndex >= images.length) return 0
      return nextIndex
    })
  }

  const textColorOnly = accentColor.split(" ").find((cls) => cls.startsWith("text-")) || "text-primary"

  return (
    <motion.div
      variants={cardVariants}
      className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start"
    >
      <div className={`relative w-full flex-shrink-0 order-1 ${reverse ? "md:order-2" : "md:order-1"}`}>
        <div
          className={`relative h-[50vh] sm:h-[55vh] md:aspect-[4/3] w-full overflow-hidden rounded-xl shadow-lg ${isDragging ? "cursor-grabbing" : "cursor-grab"} select-none bg-muted`}
        >
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={(e, { offset, velocity }) => {
                setIsDragging(false)
                const swipe = swipePower(offset.x, velocity.x)

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1)
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1)
                }
              }}
              className="absolute inset-0"
            >
              <Image
                src={images[currentIndex] || "/placeholder.svg"}
                alt={`${title} - Image ${currentIndex + 1}`}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 90vw, (max-width: 1024px) 45vw, 40vw"
                className="object-cover pointer-events-none"
                draggable={false}
                style={getImageStyle}
              />
            </motion.div>
          </AnimatePresence>

          {images.length > 1 && (
            <>
              <button
                onClick={() => paginate(-1)}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-full p-1.5 sm:p-2 shadow-lg transition-all hover:scale-110 border border-white/40"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                onClick={() => paginate(1)}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-full p-1.5 sm:p-2 shadow-lg transition-all hover:scale-110 border border-white/40"
                aria-label="Next image"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-10">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setDirection(idx > currentIndex ? 1 : -1)
                      setCurrentIndex(idx)
                    }}
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                      idx === currentIndex ? "bg-white w-4 sm:w-6" : "bg-white/50"
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className={`w-full flex-shrink-0 order-2 ${reverse ? "md:order-1 md:text-right" : "md:order-2 md:text-left"} text-left`}
      >
        <span
          className={`${textColorOnly} text-lg sm:text-3xl  uppercase font-serif font-bold inline-block`}
        >
          {category}
        </span>
        <h3 className="text-xl sm:text-2xl md:text-xl font-sans font-semibold text-foreground mt-2 mb-3 sm:mb-4">
          {title}
        </h3>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-5 sm:mb-6 font-sans">
          {description}
        </p>
        <div className={`flex ${reverse ? "justify-start md:justify-end" : "justify-start"}`}>
          <Button
            variant="ghost"
            className={`${accentColor} border-0 hover:bg-${accentColor} transition-all text-sm sm:text-base px-4 sm:px-6`}
            asChild
          >
            <a href="#contact">Learn More</a>
          </Button>
        </div>
      </div>
    </motion.div>
  )
}
